import { Skeleton } from '@ethings/ui-toolkit';
import { FC, ImgHTMLAttributes, useEffect, useState } from 'react';
import { useAuthenticated } from '../../auth';

export type EntityImageType = 'thing' | 'thing-type' | 'person' | 'point-of-interest-type';

export interface EntityImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  id: string;
  filename: string;
  type: EntityImageType;
  fallbackImage?: string;
}

export const EntityImage: FC<EntityImageProps> = ({
  id,
  filename,
  type,
  alt,
  className,
  width,
  height,
  fallbackImage,
  ...imgProps
}) => {
  const { axios } = useAuthenticated();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let isComponentMounted = true;
    let reader: FileReader | null = null;
    let handleLoadEnd: (() => void) | null = null;
    let handleError: (() => void) | null = null;

    const setFallback = (): void => {
      setImageSrc(fallbackImage ?? null);
    };

    if (!id || !filename) {
      setFallback();
      setIsLoading(false);
      return () => { isComponentMounted = false; };
    }

    const fetchImage = async (): Promise<void> => {
      setIsLoading(true);
      setImageSrc(null);

      try {
        const response = await axios.get<Blob>(`/api/v1/${type}/${id}/image/${filename}`, { transformResponse: [], responseType: 'blob' });
        if (!isComponentMounted) return;

        reader = new FileReader();

        handleLoadEnd = (): void => {
          if (!isComponentMounted) return;
          const result = reader?.result;
          if (typeof result === 'string' && result.length > 0) {
            setImageSrc(result);
          } else {
            setFallback();
          }
          setIsLoading(false);
        };

        handleError = (): void => {
          if (!isComponentMounted) return;
          setFallback();
          setIsLoading(false);
        };

        reader.addEventListener('loadend', handleLoadEnd);
        reader.addEventListener('error', handleError);
        reader.readAsDataURL(response.data);
      } catch {
        if (!isComponentMounted) return;
        setFallback();
        setIsLoading(false);
      }
    };

    void fetchImage();

    return () => {
      isComponentMounted = false;
      if (reader) {
        if (handleLoadEnd) reader.removeEventListener('loadend', handleLoadEnd);
        if (handleError) reader.removeEventListener('error', handleError);
        if (reader.readyState === FileReader.LOADING) reader.abort();
      }
    };
  }, [axios, fallbackImage, filename, id, type]);

  const skeletonWidth = width ?? 150;
  const skeletonHeight = height ?? 150;
  const resolvedAlt = alt ?? `${type} image`;

  if (isLoading || !imageSrc) {
    return (
      <Skeleton variant="rectangular" width={skeletonWidth} height={skeletonHeight} className={className} />
    );
  }

  return (
    <img {...imgProps} alt={resolvedAlt} className={className} height={height ?? 150} width={width ?? 150} src={imageSrc} />
  );
};
